// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import {
  getAnnotations, createAnnotation, updateAnnotation, deleteAnnotation,
  getSuggestions, createSuggestion, updateSuggestion,
} from '../services/api';

const LS_TAB_KEY = 'normascan_annotation_open_tab';

const AnnotationPanel = ({ analysis, onClose }) => {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const isAdminDev = ['admin', 'dev'].includes(user?.role);
  const canSeeSuggestions = ['admin', 'dev', 'partner'].includes(user?.role);
  const analysisId = analysis.id || analysis.analysis_id;
  const ANNOTATION_TYPES = {
    parsing: { label: t('ann_type_parsing'), tip: t('ann_tip_parsing') },
    display: { label: t('ann_type_display'), tip: t('ann_tip_display') },
    error:   { label: t('ann_type_error'),   tip: t('ann_tip_error') },
    anomaly: { label: t('ann_type_anomaly'), tip: t('ann_tip_anomaly') },
    other:   { label: t('ann_type_other'),   tip: t('ann_tip_other') },
  };

  const ANNOTATION_TARGETS = {
    json_pass1: t('ann_target_json1'),
    json_final: t('ann_target_json2'),
    html:       t('ann_target_html'),
    pdf:        t('ann_target_pdf'),
    general:    t('ann_target_general'),
  };

  const SUGGESTION_CATEGORIES = {
    ui:      t('sugg_panel_cat_ui'),
    parsing: t('sugg_panel_cat_parsing'),
    rules:   t('sugg_panel_cat_rules'),
    output:  t('sugg_panel_cat_output'),
    other:   t('sugg_panel_cat_other'),
  };

  const SUGGESTION_STATUSES = {
    open:        { label: t('sugg_panel_open'),        color: '#3b82f6' },
    in_progress: { label: t('sugg_panel_in_progress'), color: '#f59e0b' },
    done:        { label: t('sugg_panel_done'),        color: '#22c55e' },
    rejected:    { label: t('sugg_panel_rejected'),    color: '#ef4444' },
  };

  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem(LS_TAB_KEY);
    return (saved === 'suggestions' && !canSeeSuggestions) ? 'annotations' : (saved || 'annotations');
  });

  // Annotations state
  const [annotations, setAnnotations]         = useState([]);
  const [loadingAnn, setLoadingAnn]           = useState(false);
  const [editingId, setEditingId]             = useState(null);
  const [editContent, setEditContent]         = useState('');
  const [newType, setNewType]                 = useState('error');
  const [newTarget, setNewTarget]             = useState('general');
  const [newContent, setNewContent]           = useState('');
  const [submittingAnn, setSubmittingAnn]     = useState(false);
  const [annError, setAnnError]               = useState('');
  const [tooltip, setTooltip]                 = useState('');

  // Suggestions state
  const [suggestions, setSuggestions]         = useState([]);
  const [loadingSug, setLoadingSug]           = useState(false);
  const [newSugCategory, setNewSugCategory]   = useState('ui');
  const [newSugContent, setNewSugContent]     = useState('');
  const [submittingSug, setSubmittingSug]     = useState(false);
  const [sugError, setSugError]               = useState('');

  // Persist onglet
  useEffect(() => {
    localStorage.setItem(LS_TAB_KEY, activeTab);
  }, [activeTab]);

  const loadAnnotations = useCallback(async () => {
    setLoadingAnn(true);
    try {
      const data = await getAnnotations(analysisId);
      setAnnotations(data.annotations || []);
    } catch (e) { console.error('❌', e); }
    finally { setLoadingAnn(false); }
  }, [analysisId]);

  const loadSuggestions = useCallback(async () => {
    setLoadingSug(true);
    try {
      const data = await getSuggestions();
      setSuggestions(data.suggestions || []);
    } catch (e) { console.error('❌', e); }
    finally { setLoadingSug(false); }
  }, []);

  useEffect(() => {
    loadAnnotations();
    if (canSeeSuggestions) loadSuggestions();
  }, [loadAnnotations, loadSuggestions, canSeeSuggestions]);

  const handleSubmitAnnotation = async (e) => {
    e.preventDefault();
    setAnnError('');
    if (!newContent.trim()) { setAnnError(t('ann_content_required_msg')); return; }
    setSubmittingAnn(true);
    try {
      await createAnnotation(analysisId, { type: newType, target: newTarget, content: newContent.trim() });
      setNewContent('');
      await loadAnnotations();
    } catch (err) { setAnnError(err.message); }
    finally { setSubmittingAnn(false); }
  };

  const handleSaveEdit = async (id) => {
    if (!editContent.trim()) return;
    try {
      await updateAnnotation(id, editContent.trim());
      setEditingId(null);
      await loadAnnotations();
    } catch (err) { alert(err.message); }
  };

  const handleDeleteAnnotation = async (id) => {
    if (!window.confirm(t('ann_delete_confirm'))) return;
    try { await deleteAnnotation(id); await loadAnnotations(); }
    catch (err) { alert(err.message); }
  };

  const handleSubmitSuggestion = async (e) => {
    e.preventDefault();
    setSugError('');
    if (!newSugContent.trim()) { setSugError(t('ann_content_required_msg')); return; }
    setSubmittingSug(true);
    try {
      await createSuggestion({ category: newSugCategory, content: newSugContent.trim() });
      setNewSugContent('');
      await loadSuggestions();
    } catch (err) { setSugError(err.message); }
    finally { setSubmittingSug(false); }
  };

  const handleSugStatus = async (id, status) => {
    try { await updateSuggestion(id, { status }); await loadSuggestions(); }
    catch (err) { alert(err.message); }
  };

  const formatDate = (d) =>
    new Date(d).toLocaleString(lang === 'en' ? 'en-GB' : 'fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  const selectStyle = {
    width: '100%', padding: '6px 8px',
    borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13,
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)', display: 'flex', justifyContent: 'flex-end' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ width: '100%', maxWidth: 560, background: '#fff', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.18)' }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: '#fff', zIndex: 10 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--black)' }}>{t('panel_title')}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{analysis.filename}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }}>✕</button>
        </div>

        {/* Onglets */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
          {(canSeeSuggestions ? ['annotations', 'suggestions'] : ['annotations']).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                flex: 1, padding: '10px 0', fontSize: 14, fontWeight: 600,
                border: 'none', background: 'none', cursor: 'pointer',
                borderBottom: activeTab === tab ? '2px solid var(--blue)' : '2px solid transparent',
                color: activeTab === tab ? 'var(--blue)' : '#6b7280',
              }}
            >
              {tab === 'annotations'
                ? `${t('panel_tab_annotations')} (${annotations.length})`
                : `${t('panel_tab_suggestions')} (${suggestions.length})`}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, padding: '16px 20px', overflowY: 'auto' }}>

          {/* ===== ANNOTATIONS ===== */}
          {activeTab === 'annotations' && (
            <>
              <form onSubmit={handleSubmitAnnotation} style={{ marginBottom: 24 }}>
                <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 14 }}>{t('new_annotation')}</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <label style={{ fontSize: 12, color: '#6b7280' }}>{t('ann_type_label')}</label>
                    <select
                      value={newType}
                      onChange={e => setNewType(e.target.value)}
                      onMouseEnter={() => setTooltip(ANNOTATION_TYPES[newType]?.tip || '')}
                      onMouseLeave={() => setTooltip('')}
                      style={selectStyle}
                    >
                      {Object.entries(ANNOTATION_TYPES).map(([k, v]) => (
                        <option key={k} value={k} title={v.tip}>{v.label}</option>
                      ))}
                    </select>
                    {tooltip && (
                      <div style={{
                        position: 'absolute', bottom: '100%', left: 0, zIndex: 30,
                        background: '#1e293b', color: '#fff', padding: '6px 10px',
                        borderRadius: 6, fontSize: 11, maxWidth: 240, marginBottom: 4,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      }}>
                        {tooltip}
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, color: '#6b7280' }}>{t('ann_target_label')}</label>
                    <select value={newTarget} onChange={e => setNewTarget(e.target.value)} style={selectStyle}>
                      {Object.entries(ANNOTATION_TARGETS).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <textarea
                  value={newContent}
                  onChange={e => setNewContent(e.target.value)}
                  placeholder={t('ann_content_ph')}
                  rows={3}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
                />
                {annError && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{t('ann_content_required')}</p>}
                <Button type="submit" disabled={submittingAnn} style={{ marginTop: 8 }}>
                  {submittingAnn ? t('btn_sending') : t('btn_add_annotation')}
                </Button>
              </form>

              {loadingAnn ? (
                <p style={{ color: '#6b7280', fontSize: 13 }}>{t('ann_loading')}</p>
              ) : annotations.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: '#9ca3af', fontSize: 13 }}>
                  {t('ann_empty')}
                </div>
              ) : annotations.map(ann => (
                <div key={ann.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', marginBottom: 10, background: '#fafafa' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ background: '#eff6ff', color: 'var(--blue)', fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 99 }}>
                        {ANNOTATION_TYPES[ann.type]?.label || ann.type}
                      </span>
                      <span style={{ background: '#f3f4f6', color: '#374151', fontSize: 11, padding: '2px 7px', borderRadius: 99 }}>
                        {ANNOTATION_TARGETS[ann.target] || ann.target}
                      </span>
                      {ann.ia_generated === 1 && (
                        <span style={{ background: '#fef3c7', color: '#92400e', fontSize: 11, padding: '2px 7px', borderRadius: 99 }}>🤖 IA</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {(isAdminDev || ann.user_id === user?.id) && ann.ia_generated !== 1 && (
                        <>
                          <button onClick={() => { setEditingId(ann.id); setEditContent(ann.content); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#6b7280' }} title="Modifier">✏️</button>
                          <button onClick={() => handleDeleteAnnotation(ann.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#ef4444' }} title="Supprimer">🗑️</button>
                        </>
                      )}
                    </div>
                  </div>
                  {editingId === ann.id ? (
                    <div style={{ marginTop: 8 }}>
                      <textarea
                        value={editContent}
                        onChange={e => setEditContent(e.target.value)}
                        rows={3}
                        style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
                      />
                      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                        <Button size="sm" onClick={() => handleSaveEdit(ann.id)}>{t('btn_save_annotation')}</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>{t('pref_cancel')}</Button>
                      </div>
                    </div>
                  ) : (
                    <p style={{ marginTop: 8, fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{ann.content}</p>
                  )}
                  <p style={{ marginTop: 6, fontSize: 11, color: '#9ca3af' }}>
                    {formatDate(ann.created_at)}
                    {ann.updated_at && ` ${t('ann_modified')} ${formatDate(ann.updated_at)}`}
                  </p>
                </div>
              ))}
            </>
          )}

          {/* ===== SUGGESTIONS ===== */}
          {activeTab === 'suggestions' && (
            <>
              <form onSubmit={handleSubmitSuggestion} style={{ marginBottom: 24 }}>
                <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 14 }}>{t('new_suggestion')}</div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 12, color: '#6b7280' }}>{t('sugg_category_label')}</label>
                  <select value={newSugCategory} onChange={e => setNewSugCategory(e.target.value)} style={selectStyle}>
                    {Object.entries(SUGGESTION_CATEGORIES).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                <textarea
                  value={newSugContent}
                  onChange={e => setNewSugContent(e.target.value)}
                  placeholder={t('sugg_content_ph')}
                  rows={3}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
                />
                {sugError && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{t('sugg_content_required')}</p>}
                <Button type="submit" disabled={submittingSug} style={{ marginTop: 8 }}>
                  {submittingSug ? t('btn_sending') : t('btn_submit_suggestion')}
                </Button>
              </form>

              {loadingSug ? (
                <p style={{ color: '#6b7280', fontSize: 13 }}>⏳ {t('loading_more')}</p>
              ) : suggestions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: '#9ca3af', fontSize: 13 }}>{t('no_suggestions')}</div>
              ) : suggestions.map(sug => {
                const sc = SUGGESTION_STATUSES[sug.status] || SUGGESTION_STATUSES.open;
                return (
                  <div key={sug.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', marginBottom: 10, background: '#fafafa' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ background: '#eff6ff', color: 'var(--blue)', fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 99 }}>
                        {SUGGESTION_CATEGORIES[sug.category] || sug.category}
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 99, background: sc.color + '20', color: sc.color }}>
                        {sc.label}
                      </span>
                    </div>
                    <p style={{ marginTop: 8, fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{sug.content}</p>
                    {sug.admin_reply && (
                      <div style={{ marginTop: 8, padding: '8px 10px', background: '#f0fdf4', borderRadius: 6, borderLeft: '3px solid #22c55e' }}>
                        <p style={{ fontSize: 11, fontWeight: 600, color: '#15803d', marginBottom: 2 }}>{t('team_reply_label')}</p>
                        <p style={{ fontSize: 12, color: '#374151' }}>{sug.admin_reply}</p>
                      </div>
                    )}
                    {isAdminDev && (
                      <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {Object.entries(SUGGESTION_STATUSES).map(([k, v]) => (
                          <button
                            key={k}
                            onClick={() => handleSugStatus(sug.id, k)}
                            disabled={sug.status === k}
                            style={{
                              fontSize: 11, padding: '2px 8px', borderRadius: 99, cursor: 'pointer',
                              border: `1px solid ${v.color}`,
                              background: sug.status === k ? v.color : 'transparent',
                              color: sug.status === k ? '#fff' : v.color,
                              opacity: sug.status === k ? 1 : 0.75,
                            }}
                          >
                            {v.label}
                          </button>
                        ))}
                      </div>
                    )}
                    <p style={{ marginTop: 6, fontSize: 11, color: '#9ca3af' }}>{formatDate(sug.created_at)}</p>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnnotationPanel;